import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle, ShieldCheck, LockSimple, HandHeart, SealCheck } from "@phosphor-icons/react";

export default function PublicLandingPage() {
    const { slug } = useParams();
    const [params] = useSearchParams();
    const [page, setPage] = useState(null);
    const [err, setErr] = useState("");
    const [values, setValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [formErr, setFormErr] = useState("");
    const [done, setDone] = useState(null);

    useEffect(() => {
        api.get(`/public/landing/${slug}`)
            .then(({ data }) => setPage(data))
            .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "This page is not available."));
    }, [slug]);

    if (err) return <Centered><SealCheck size={44} className="mx-auto text-stone-300" /><h1 className="mt-4 font-heading text-xl font-bold text-ink">Page unavailable</h1><p className="mt-2 text-sm text-stone-600">{err}</p></Centered>;
    if (!page) return <Centered><div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-200 border-t-stone-500" /></Centered>;

    const b = page.branding || {};
    const color = b.brand_color || "#FF5A36";
    const fields = page.form?.fields || [];
    const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setFormErr("");
        try {
            const attribution = {
                source: params.get("source") || (document.referrer ? "referral" : "direct"),
                utm_source: params.get("utm_source") || "",
                utm_medium: params.get("utm_medium") || "",
                utm_campaign: params.get("utm_campaign") || "",
                platform: params.get("platform") || params.get("utm_source") || "",
                referrer: document.referrer || "",
            };
            const { data } = await api.post(`/public/landing/${slug}/submit`, { data: values, attribution });
            setDone(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e2) { setFormErr(formatApiError(e2?.response?.data?.detail) || "Something went wrong. Please try again."); }
        finally { setBusy(false); }
    };

    const TRUST = [
        { Icon: LockSimple, t: "Encrypted & secure", d: "Your details are transmitted securely." },
        { Icon: ShieldCheck, t: "Kept private", d: "We never sell or share your information." },
        { Icon: HandHeart, t: "No spam", d: "We only reach out about your request." },
    ];

    return (
        <div className="min-h-screen bg-paper" data-testid="public-landing">
            {/* header */}
            <header className="sticky top-0 z-40 border-b border-stone-200/80 bg-white/85 backdrop-blur-md">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
                    {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-8 object-contain" /> : <span className="font-heading text-lg font-bold text-ink">{page.name}</span>}
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-[11px] font-semibold text-stone-500"><LockSimple size={13} weight="fill" /> Secure form</span>
                </div>
            </header>

            {done ? (
                <div className="mx-auto max-w-lg px-4 py-16 sm:py-24">
                    <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-xl shadow-stone-900/5 ring-1 ring-stone-900/5" data-testid="lp-thankyou">
                        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full" style={{ background: `${color}1a` }}>
                            <CheckCircle size={40} weight="fill" style={{ color }} />
                        </div>
                        <h1 className="mt-5 font-heading text-3xl font-bold tracking-tight text-ink">{done.thankyou_heading}</h1>
                        <p className="mt-3 text-sm leading-relaxed text-stone-600">{done.thankyou_message}</p>
                    </div>
                </div>
            ) : (() => {
                const layout = b.layout || "banner_top";
                const story = (
                    <>
                        <div className="text-[12px] font-bold uppercase tracking-[0.18em]" style={{ color }}>You're one step away</div>
                        <h1 className="mt-3 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-ink lg:text-5xl">{b.heading || page.name}</h1>
                        {b.subheading && <p className="mt-4 text-lg font-medium leading-relaxed text-stone-600">{b.subheading}</p>}
                        {b.body && <p className="mt-4 whitespace-pre-wrap text-[15px] leading-relaxed text-stone-500">{b.body}</p>}
                        <div className="mt-8 grid gap-3 sm:grid-cols-3">
                            {TRUST.map(({ Icon, t, d }) => (
                                <div key={t} className="rounded-2xl border border-stone-200/70 bg-white/60 p-4">
                                    <Icon size={20} weight="duotone" style={{ color }} />
                                    <div className="mt-2 text-sm font-bold text-ink">{t}</div>
                                    <div className="mt-0.5 text-xs leading-normal text-stone-500">{d}</div>
                                </div>
                            ))}
                        </div>
                    </>
                );
                const formCard = (
                    <div className="rounded-2xl border border-stone-200/90 bg-white p-6 shadow-xl shadow-stone-900/5 ring-1 ring-stone-900/5 sm:p-8">
                        <div className="mb-5">
                            <h2 className="font-heading text-xl font-bold tracking-tight text-ink">{b.cta_text && b.cta_text !== "Submit" ? b.cta_text : "Request details"}</h2>
                            <p className="mt-1 text-sm text-stone-500">Fill in the form and we'll be in touch.</p>
                        </div>
                        {page.form ? (
                            <form onSubmit={submit} className="space-y-4" data-testid="lp-form">
                                {fields.map((f) => <PublicField key={f.id} f={f} value={values[f.id]} onChange={(v) => set(f.id, v)} color={color} />)}
                                {formErr && <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="lp-error">{formErr}</div>}
                                <button type="submit" disabled={busy} data-testid="lp-submit"
                                    className="w-full rounded-xl px-4 py-3 text-sm font-bold text-white shadow-sm transition-all duration-150 hover:shadow-md active:scale-[0.99] disabled:opacity-60"
                                    style={{ background: color }}>
                                    {busy ? "Submitting…" : (b.cta_text || "Submit")}
                                </button>
                                <p className="flex items-center justify-center gap-1.5 pt-1 text-[11px] text-stone-400"><LockSimple size={12} /> Your information is kept private and secure.</p>
                            </form>
                        ) : <p className="text-sm text-stone-400">This page has no form attached.</p>}
                    </div>
                );

                if (layout === "banner_top") {
                    return (
                        <>
                            {b.banner_url && (
                                <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
                                    <img src={b.banner_url} alt="" className="max-h-[440px] w-full rounded-2xl bg-white object-contain shadow-sm ring-1 ring-stone-900/5" />
                                </div>
                            )}
                            <main className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-8 px-4 pb-16 pt-8 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:-mt-14">
                                <div className="lg:col-span-7 lg:pt-24">{story}</div>
                                <div className="lg:relative lg:z-10 lg:col-span-5">{formCard}</div>
                            </main>
                        </>
                    );
                }
                // split (banner in left column)
                return (
                    <main className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-8 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:py-16">
                        <div className="lg:col-span-7 lg:pt-6">
                            {b.banner_url && <img src={b.banner_url} alt="" className="mb-8 aspect-[16/7] w-full rounded-2xl object-cover shadow-sm ring-1 ring-stone-900/5" />}
                            {story}
                        </div>
                        <div className="lg:col-span-5">{formCard}</div>
                    </main>
                );
            })()}
            <footer className="pb-10 pt-2 text-center text-[11px] text-stone-400">Powered by Inspiro Connect</footer>
        </div>
    );
}

function PublicField({ f, value, onChange, color }) {
    const req = !!f.required;
    const lbl = <label className="mb-1.5 block text-sm font-semibold text-ink">{f.label}{req && <span style={{ color }}> *</span>}</label>;
    const cls = "w-full rounded-xl border border-stone-200 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-stone-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10 transition-all";
    const inputType = { email: "email", phone: "tel", number: "number", text: "text" }[f.type] || "text";

    if (f.type === "textarea") return <div>{lbl}<textarea data-testid={`lp-field-${f.id}`} required={req} rows={4} placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></div>;
    if (f.type === "dropdown") return <div>{lbl}<select data-testid={`lp-field-${f.id}`} required={req} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}><option value="">{f.placeholder || "Select…"}</option>{(f.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}</select></div>;
    if (f.type === "radio") return <div>{lbl}<div className="space-y-1.5" data-testid={`lp-field-${f.id}`}>{(f.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm text-stone-600"><input type="radio" name={f.id} required={req} checked={value === o} onChange={() => onChange(o)} style={{ accentColor: color }} /> {o}</label>)}</div></div>;
    if (f.type === "checkbox") { const arr = Array.isArray(value) ? value : []; return <div>{lbl}<div className="space-y-1.5" data-testid={`lp-field-${f.id}`}>{(f.options || []).map((o, i) => <label key={i} className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={arr.includes(o)} onChange={(e) => onChange(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} style={{ accentColor: color }} /> {o}</label>)}</div></div>; }
    if (f.type === "consent") return <label className="flex items-start gap-2.5 rounded-xl bg-stone-50 p-3 text-xs leading-relaxed text-stone-600"><input data-testid={`lp-field-${f.id}`} type="checkbox" required={req} checked={value === true} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" style={{ accentColor: color }} /> <span>{f.label || "I agree to be contacted."}</span></label>;
    return <div>{lbl}<input data-testid={`lp-field-${f.id}`} type={inputType} required={req} placeholder={f.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></div>;
}

const Centered = ({ children }) => <div className="grid min-h-screen place-items-center bg-paper p-4"><div className="text-center">{children}</div></div>;
